import { toast } from 'react-toastify';
import {
  CLICK_ACTIONS,
  REIMBURSEMENT_CLIAM_TYPES,
  ReimbursementClaimType,
} from '../../utils/Constant';
import {
  AccomodationFromData,
  ApproveReimbursementFilterType,
  ClaimType,
  ColumnHeaderType,
  FoodFromData,
  MiscellaneousFromData,
  TravelFromData,
} from '../../utils/Types';
import {
  blankValueGetter,
  convertDateToISODate,
  dateValueGetterWithTime,
  formatNumberWithComma,
  handleApproveReimbursementActionColors,
  isAPIFailed,
  isAPISuccess,
} from '../../utils/Util';
import moment from 'moment';
import { ThunkDispatch } from 'redux-thunk';
import {
  fetchReimbursementLocationDetails,
  postApplyReimbursementAttachments,
} from '../../features/reimbursementSlice';

export const ApplyReimbursementColumnHeader: ColumnHeaderType[] = [
  {
    name: 'Task Id',
    field: 'taskId',
    sortable: false,
    clickable: false,
    action: CLICK_ACTIONS.VIEW_TASK,
    valueGetter: blankValueGetter,
  },
  {
    name: 'Task Name',
    field: 'taskName',
    sortable: false,
    valueGetter: blankValueGetter,
  },
  {
    name: 'Source',
    field: 'sourceName',
    sortable: true,
    valueGetter: blankValueGetter,
  },
  {
    name: 'Action',
    field: 'action',
    clickable: true,
    action: CLICK_ACTIONS.APPLY_REIMBURSEMENT,
    value: 'Raise a claim',
  },
];
export const ApproveReimbursementColumnHeader: ColumnHeaderType[] = [
  {
    name: 'Claim Id',
    field: 'claimId',
    showProfilePhoto: true,
    clickable: false,
    valueGetter: (data: any, field?: string) => {
      const value = data[field as keyof any];
      return value?.toString()?.padStart(4, '0') || '-';
    },
  },
  {
    name: 'Employee Id',
    field: 'employeeUniqueId',
    sortable: false,
    clickable: false,
    valueGetter: blankValueGetter,
  },
  {
    name: 'Employee Name',
    field: 'fullName',
    sortable: false,
  },
  {
    name: 'Zone',
    field: 'zone',
    sortable: false,
    valueGetter: blankValueGetter,
    valueFromLocalStorage: true,
  },
  {
    name: 'State',
    field: 'state',
    clickable: false,
    valueGetter: blankValueGetter,
    valueFromLocalStorage: true,
  },
  {
    name: 'District',
    field: 'district',
    clickable: false,
    valueGetter: blankValueGetter,
    valueFromLocalStorage: true,
  },

  {
    name: 'Claim Date & Time',
    field: 'createdOn',
    clickable: false,
    valueGetter: dateValueGetterWithTime,
  },
  {
    name: 'Type of Claim',
    field: 'claimType',
    clickable: false,
    valueGetter: blankValueGetter,
    toolTipAfter: 25,
  },
  {
    name: 'Claim Amount',
    field: 'totalClaimAmount',
    clickable: false,
    valueGetter: (data: any, field?: string) => {
      const value = data[field as keyof any];
      return formatNumberWithComma(value);
    },
  },
  {
    name: 'Status',
    field: 'statusName',
    clickable: true,
    action: CLICK_ACTIONS.APPROVE_REIMBURSEMENT,
    // actionGetter: approveReimbursementActionGetter,
    // valueGetter: blankValueGetter,
    classNameGetter: handleApproveReimbursementActionColors,
  },
];
const validateFields = (fields: any[]) => {
  return fields.every((field: any) => {
    if (typeof field === 'string') {
      return field.trim();
    }
    return Number(field);
  });
};

const validateImages = (imagesList: any, type: ReimbursementClaimType) => {
  return imagesList?.[REIMBURSEMENT_CLIAM_TYPES[type]]?.length;
};

const showError = (message: string) => {
  toast.error(message);
  return false;
};

export const validateSection = (
  imagesList: any,
  fields: any,
  imagesType: any,
  errorMessageFields: string,
  errorMessageImages: string
) => {
  if (!validateFields(fields)) {
    return showError(errorMessageFields);
  }
  if (!validateImages(imagesList, imagesType)) {
    return showError(errorMessageImages);
  }
  return true;
};

export const validation = (
  imagesList: any,
  claimType: ClaimType,
  travelFormData: TravelFromData,
  foodFormData: FoodFromData,
  accommodation: AccomodationFromData,
  miscellaneous: MiscellaneousFromData
) => {
  if (claimType?.Travel) {
    const { locationA, locationB, totalDistance, totalExpenses, reason } =
      travelFormData || {};
    if (
      !validateSection(
        imagesList,
        [locationA, locationB, totalDistance, totalExpenses, reason],
        'Travel',
        'Please fill in all mandatory fields in the Travel section.',
        'Please upload the required images for the Travel section.'
      )
    )
      return false;
  }

  if (claimType?.Food) {
    const { locationA, totalExpenses } = foodFormData || {};
    if (
      !validateSection(
        imagesList,
        [locationA, totalExpenses],
        'Food',
        'Please fill in all the mandatory fields in the Food section.',
        'Please upload the required images for the Food section.'
      )
    )
      return false;
  }

  if (claimType?.Accommodation) {
    if (
      moment(accommodation?.claimFromDate, 'DD-MM-YYYY hh:mm a')?.isAfter(
        moment(accommodation?.claimToDate, 'DD-MM-YYYY hh:mm a')
      )
    ) {
      return showError(
        'In Accommodation, Claim From Date cannot be greater than Claim To Date'
      );
    }
    const {
      claimFromDate,
      claimToDate,
      totalExpenses,
      reason,
      locationA,
      zone,
      state,
      district,
      city,
      hotelName,
    } = accommodation || {};
    if (
      !validateSection(
        imagesList,
        [
          claimFromDate,
          claimToDate,
          totalExpenses,
          reason,
          locationA,
          zone,
          state,
          district,
          city,
          hotelName,
        ],
        'Accommodation',
        'Please fill in all the mandatory fields in the Accommodation section',
        'Please upload the required images for the Accommodation section.'
      )
    )
      return false;
  }

  if (claimType?.Miscellaneous) {
    const { claimFromDate, locationA, totalExpenses } = miscellaneous || {};
    if (
      !validateSection(
        imagesList,
        [claimFromDate, locationA, totalExpenses],
        'Miscellaneous',
        'Please fill in all the mandatory fields in the Miscellaneous section',
        'Please upload the required images for the Miscellaneous section.'
      )
    )
      return false;
  }
  return true;
};
export const getReqData = (
  imagesList: any,
  claimType: ClaimType,
  travelFormData: TravelFromData,
  foodFormData: FoodFromData,
  accommodation: AccomodationFromData,
  miscellaneous: MiscellaneousFromData,
  selectedTask: any
) => {
  const claimDetails = [];
  if (claimType?.Travel) {
    claimDetails.push({
      ...travelFormData,
      claimFromDate: convertDateToISODate(travelFormData?.claimFromDate),
    });
  }
  if (claimType?.Food) {
    claimDetails.push({
      ...foodFormData,
      claimFromDate: convertDateToISODate(foodFormData?.claimFromDate),
    });
  }
  if (claimType?.Accommodation) {
    claimDetails.push({
      ...accommodation,
      claimFromDate: convertDateToISODate(accommodation?.claimFromDate),
      claimToDate: convertDateToISODate(accommodation?.claimToDate),
    });
  }
  if (claimType?.Miscellaneous) {
    claimDetails.push({
      ...miscellaneous,
      claimFromDate: convertDateToISODate(miscellaneous?.claimFromDate),
    });
  }

  const formData = new FormData();
  const allSelectedFiles = Object.values(imagesList).flat();
  allSelectedFiles.forEach((file: any) => {
    formData.append('files', file);
  });
  const data = {
    taskAssignmentId: selectedTask?.taskAssignmentId || null,
    claimDetails: claimDetails,
  };
  return { data, allSelectedFiles, formData };
};

export const handleFileChange = async (
  event: React.ChangeEvent<HTMLInputElement>,
  imagesList: any,
  activeClaimUpload: string | number,
  setImagesList: (value: any) => void,
  setActiveClaimUpload: any,
  fileInputRef: any
) => {
  const files = Array.from(event.target.files || []);
  if (files && files.length > 0) {
    const filesCount =
      (imagesList?.[activeClaimUpload as keyof any]?.length || 0) +
      files?.length;
    if (filesCount > 9) {
      toast.error('You can only upload up to 9 images.');
      return;
    }
    let isGreaterThanMaxFileSize = false;
    const updatedFiles = files.map((file: any) => {
      if (file?.size > 1024 * 1024) {
        toast.error(
          'Each file must not exceed 1MB. Please upload smaller files.'
        );
        isGreaterThanMaxFileSize = true;
        return;
      }
      const newFileName =
        moment().format('YYYYMMDDSSSSS') +
        `_CT${activeClaimUpload}` +
        file.name.slice(file.name.lastIndexOf('.'));
      return new File([file], newFileName, { type: file.type });
    });
    if (isGreaterThanMaxFileSize) {
      return;
    }
    setImagesList({
      ...imagesList,
      [activeClaimUpload]: [
        ...(imagesList?.[activeClaimUpload] || []),
        ...updatedFiles,
      ],
    });
    setActiveClaimUpload('');
    event.target.value = '';
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.type = 'file';
    }
  }
};

export const reimbursementLocationDetails = (
  setShowLoader: (isLoading: boolean) => void,
  dispatch: ThunkDispatch<any, any, any>,
  setDisableTravelField: (value: any) => void,
  setTravelFormData: (value: any) => void,
  selectedTask: any
) => {
  setShowLoader(true);
  dispatch(fetchReimbursementLocationDetails({ taskId: selectedTask?.taskId }))
    ?.then((fulfilled: any) => {
      setShowLoader(false);
      if (isAPISuccess(fulfilled?.payload)) {
        const travelApiData: any = {
          totalDistance: fulfilled?.payload?.result?.totalDistance || '',
          locationA: fulfilled?.payload?.result?.startPointAddress || '',
          locationB: fulfilled?.payload?.result?.endPointAddress || '',
        };
        if (fulfilled?.payload?.result?.completionDate) {
          travelApiData.claimFromDate = moment(
            fulfilled?.payload?.result?.completionDate
          ).format('DD-MM-YYYY H:mm');
        }
        setDisableTravelField(travelApiData);
        setTravelFormData((prev: any) => ({
          ...prev,
          ...travelApiData,
        }));
      }
    })
    ?.catch(() => {
      setShowLoader(false);
    });
};
export const uploadAttachments = (
  formData: any,
  params: any,
  setShowLoader: (isLoading: boolean) => void,
  dispatch: ThunkDispatch<any, any, any>,
  toggleModal: (action: string) => void
) => {
  setShowLoader(true);
  dispatch(
    postApplyReimbursementAttachments({ data: formData, params: params })
  )
    ?.then((fulfilled: any) => {
      setShowLoader(false);
      if (isAPIFailed(fulfilled?.payload)) {
        toast.error(fulfilled?.payload.responseInformation.responseMessage);
      } else if (isAPISuccess(fulfilled?.payload)) {
        toast.success(fulfilled?.payload.responseInformation.responseMessage);
        toggleModal(CLICK_ACTIONS.NO_ACTION);
      }
    })
    ?.catch(() => {
      setShowLoader(false);
    });
};

export const handlePayloadForApproveList = (
  currentPage: number,
  pageSize: number,
  filter?: ApproveReimbursementFilterType
) => {
  const data: any = {
    pageNumber: currentPage,
    pagePerCount: pageSize,
  };
  if (filter) {
    if (filter?.employeeName) {
      data.employeeName = filter.employeeName;
    }
    if (filter.employeeId) {
      data.employeeUniqueId = filter.employeeId;
    }
    if (filter?.zone?.value) {
      data.zone = Number(filter.zone.value);
    }
    if (filter?.state?.value) {
      data.state = Number(filter.state.value);
    }
    if (filter?.district?.value) {
      data.district = Number(filter.district.value);
    }
    if (filter?.city?.value) {
      data.city = Number(filter.city.value);
    }
    if (filter?.role?.value) {
      data.role = filter.role.value;
    }
    if (filter?.status?.value) {
      data.status = filter.status.value;
    }
  }

  return data;
};

export const onTableAction = (
  param: any,
  columnHeader: ColumnHeaderType,
  setSelectedTask: any,
  setIsModalOpen: any
) => {
  setSelectedTask(param);
  setIsModalOpen(true);
};

export const handleToggleModal = (
  action: any,
  setIsSuccessfullyApproveOpen: any,
  setIsModalOpen: any,
  getApproveReimbursementDataList: any,
  setSelectedTask: any,
  selectedFilter: any
) => {
  if (action === CLICK_ACTIONS?.REIMBURSEMENT_SUCCESS_MODAL) {
    setIsSuccessfullyApproveOpen(true);
    setIsModalOpen(false);
  } else {
    getApproveReimbursementDataList(selectedFilter);
    setIsModalOpen(false);
    setSelectedTask(null);
  }
};

export const handleApproveSuccessToggle = (
  setIsSuccessfullyApproveOpen: (value: any) => void,
  toggleModal: any,
  isSuccessfullyApproveOpen: boolean
) => {
  setIsSuccessfullyApproveOpen(!isSuccessfullyApproveOpen);
  toggleModal();
};
export const mediaUploadHandleFiles = (
  fileArray: File[],
  supportedFiles: string[],
  selectedFiles: File[],
  maxFileSizeInBytes: number | undefined,
  maxFiles: number,
  setSelectedFiles: (arg: any[]) => void
) => {
  const supportedFileArray = fileArray.filter((file) => {
    return supportedFiles.includes(
      `.${file.name.split('.')?.pop()?.toLowerCase()}`
    );
  });
  const unsupportedFileArray = fileArray.filter(
    (file) =>
      !supportedFiles.includes(`.${file.name.split('.')?.pop()?.toLowerCase()}`)
  );

  if (unsupportedFileArray.length > 0) {
    toast.error(`Please upload only ${supportedFiles.join(', ')} ${' '}files.`);
    setSelectedFiles([]);
    return;
  }

  if (selectedFiles.length + supportedFileArray.length > maxFiles) {
    toast.error(
      `You can only upload up to ${maxFiles} ${maxFiles === 1 ? 'file' : 'files'}.`
    );
    return;
  }
  if (maxFileSizeInBytes) {
    let exceededFileSize = false;
    supportedFileArray.forEach((file: File) => {
      if (file.size > maxFileSizeInBytes) {
        toast.error(
          `Please ensure each file is smaller than ${maxFileSizeInBytes / (1024 * 1024)}MB `
        );
        exceededFileSize = true;
        return;
      }
    });
    if (exceededFileSize) return;
  }
  setSelectedFiles([...selectedFiles, ...supportedFileArray]);
};
export const createFileWithSize = (
  sizeInBytes: number,
  name: string,
  type: string
) => {
  const content = 'a'.repeat(sizeInBytes);
  return new File([content], name, { type });
};
